<script lang="ts">
  import { blur, slide } from 'svelte/transition';

  type Background = 'default' | 'subtle';

  interface Props {
    label?: string;
    background?: Background;
    name?: string;
    value?: string | null | undefined;
    placeholder?: string;
    validationError?: string | undefined | null;
    validationSuccess?: string | undefined;
    disabled?: boolean;
    pending?: boolean;
    dirty?: boolean;
    centered?: boolean;
    subLabel?: string | undefined;
    /**
     * Nasty shortcut to make dirty flag work nice with first async debounced validation.
     * The problem is when input is clean, no keydown events were emitted we don't want to show errors.
     * Now if we start typing, we trigger debounce async validations, but keys were pressed.
     * You'll see an error message blink in that scenario. This is a hack to mitigate this issue.
     *
     * Otherwise, we have to plug dirty flag into async validations which happen above this component.
     */
    dirtyDebounce?: number;
    testId?: string;
    testIdValidation?: string;
    leading?: import('svelte').Snippet;
    trailing?: import('svelte').Snippet;
    button?: import('svelte').Snippet;
    onfocus?: () => void;
    onblur?: () => void;
    onclick?: () => void;
    onpaste?: (e: ClipboardEvent & { currentTarget: EventTarget & HTMLInputElement }) => void;
    onkeyup?: () => void;
    oninput?: () => void;
    onkeydown?: (e: Event) => void;
  }

  export const markAsDirty = () => {
    dirty = true;
  };

  export const focus = () => {
    inputElement?.focus();
  };

  let {
    label = '',
    background = 'default',
    name = '',
    value = $bindable(''),
    placeholder = '',
    validationError = $bindable(undefined),
    validationSuccess = $bindable(undefined),
    disabled = false,
    pending = false,
    centered = false,
    dirty = $bindable(false),
    subLabel = undefined,
    dirtyDebounce = 0,
    testId = '',
    testIdValidation = '',
    leading,
    trailing,
    button,
    onfocus,
    onblur,
    onclick,
    onpaste,
    onkeyup,
    oninput,
    onkeydown,
  }: Props = $props();

  let inputElement: HTMLInputElement | null = $state(null);

  const onKeydown = (event: Event) => {
    setTimeout(() => {
      dirty = true;
    }, dirtyDebounce);
    onkeydown?.(event);
  };

  const BACKGROUND: Record<Background, string> = {
    default: 'bg-background',
    subtle: 'bg-background-subtle',
  };

  let backgroundClass = $derived(BACKGROUND[background]);

  let valid = $derived(!!validationSuccess);

  let invalid = $derived((validationError ?? '').length > 0);
</script>

<div>
  <span class="mb-2 typography-body-m-medium">{label}</span>
  <div class="flex w-full gap-2">
    <div
      class="relative grid h-13 grow grid-cols-[auto_1fr_auto]
  overflow-hidden rounded-xl {backgroundClass} border border-border
  after:pointer-events-none after:absolute after:-inset-px
  after:block after:h-[calc(100%+2px)] after:w-[calc(100%+2px)]
  after:rounded-xl after:border-2 after:border-border-accent
  after:opacity-0 after:duration-200 after:content-['']
  focus-within:after:opacity-100"
      class:border-border-muted={disabled}
      class:border-border-danger-emphasis={dirty && invalid}
      class:after:border-border-danger-emphasis={dirty && invalid}
      class:border-border-success-emphasis={dirty && valid}
      class:after:border-border-success-emphasis={dirty && valid}
      class:border-opacity-25={pending}
      class:after:border-opacity-25={pending}>
      <div class="flex h-full items-center">
        {@render leading?.()}
      </div>
      <input
        {onfocus}
        {onblur}
        {onclick}
        onpaste={(e) => {
          onpaste?.(e);
          onKeydown(e);
        }}
        {onkeyup}
        {oninput}
        onkeydown={onKeydown}
        bind:this={inputElement}
        bind:value
        {name}
        {disabled}
        {placeholder}
        data-1p-ignore
        type="text"
        autocomplete="off"
        autocapitalize="none"
        class:pl-2={!leading}
        class:pr-2={!trailing}
        class:text-center={centered}
        class="h-full w-full rounded-none border-none bg-transparent px-0 py-2
    text-left text-foreground outline-none
    ring-transparent focus:border-none focus:outline-none
    focus:ring-transparent disabled:opacity-50"
        aria-invalid={invalid}
        data-testid={testId} />
      <div class="flex h-full items-center">
        {@render trailing?.()}
      </div>
    </div>
    {@render button?.()}
  </div>
  {#if subLabel && !validationError && !validationSuccess}
    <div transition:slide>
      <span
        transition:blur
        class="typography-body-s-regular mt-1.5 block text-foreground-muted"
        data-testid={testIdValidation}>
        {subLabel}
      </span>
    </div>
  {/if}
  {#if dirty && validationError?.length}
    <div transition:slide>
      <span
        transition:blur
        class="typography-body-s-regular text-foreground-danger"
        data-testid={testIdValidation}>
        {validationError}
      </span>
    </div>
  {/if}
  {#if dirty && validationSuccess?.length}
    <div transition:slide>
      <span transition:blur class="typography-body-s-regular text-foreground-success">
        {validationSuccess}
      </span>
    </div>
  {/if}
</div>
